// src/services/conversationStorageService.js
// Service for managing conversation history and persistence

const STORAGE_KEYS = {
  CONVERSATIONS: 'conversationHistory',
  ACTIVE_CONVERSATION: 'activeConversationId',
  USER_PREFERENCES: 'chatUserPreferences'
};

export class ConversationStorageService {
  /**
   * Get all conversations for the current user
   */
  static getConversations() {
    try {
      const conversations = localStorage.getItem(STORAGE_KEYS.CONVERSATIONS);
      const parsed = conversations ? JSON.parse(conversations) : [];
      
      // Sort by last activity (most recent first)
      return parsed.sort((a, b) => new Date(b.lastActivity) - new Date(a.lastActivity));
    } catch (error) {
      console.error('Error loading conversations:', error);
      return [];
    }
  }

  /**
   * Save conversations to localStorage
   */
  static saveConversations(conversations) {
    try {
      // Sort by last activity before saving
      const sortedConversations = conversations.sort((a, b) => 
        new Date(b.lastActivity) - new Date(a.lastActivity)
      );
      
      localStorage.setItem(STORAGE_KEYS.CONVERSATIONS, JSON.stringify(sortedConversations));
      return true;
    } catch (error) {
      console.error('Error saving conversations:', error);
      return false;
    }
  }

  /**
   * Create a new conversation
   */
  static createConversation(title, userId) {
    const sessionId = `session-${userId}-${Date.now()}`;
    const conversation = {
      sessionId,
      title: title || `New Conversation ${new Date().toLocaleDateString()}`,
      createdAt: new Date().toISOString(),
      lastActivity: new Date().toISOString(),
      messageCount: 0,
      userId: userId,
      isActive: true
    };

    const conversations = this.getConversations();
    const updatedConversations = [conversation, ...conversations];
    this.saveConversations(updatedConversations);
    
    return conversation;
  }

  /**
   * Update conversation activity
   */
  static updateConversationActivity(sessionId, updates = {}) {
    const conversations = this.getConversations();
    const updatedConversations = conversations.map(conv => {
      if (conv.sessionId === sessionId) {
        return {
          ...conv,
          ...updates,
          lastActivity: new Date().toISOString()
        };
      }
      return conv;
    });

    this.saveConversations(updatedConversations);
    return updatedConversations.find(conv => conv.sessionId === sessionId);
  }

  /**
   * Update conversation session ID (for when temporary sessions get real IDs)
   */
  static updateConversationSessionId(oldSessionId, newSessionId) {
    const conversations = this.getConversations();
    const updatedConversations = conversations.map(conv => {
      if (conv.sessionId === oldSessionId) {
        return {
          ...conv,
          sessionId: newSessionId,
          isTemporary: false,
          lastActivity: new Date().toISOString()
        };
      }
      return conv;
    });

    this.saveConversations(updatedConversations);
    
    // Also migrate any cached messages
    const oldMessages = this.getCachedConversationMessages(oldSessionId);
    if (oldMessages) {
      this.cacheConversationMessages(newSessionId, oldMessages);
      this.clearConversationMessages(oldSessionId);
    }
    
    // Update active conversation ID if it matches
    if (this.getActiveConversationId() === oldSessionId) {
      this.setActiveConversationId(newSessionId);
    }
    
    return updatedConversations.find(conv => conv.sessionId === newSessionId);
  }

  /**
   * Delete a conversation
   */
  static deleteConversation(sessionId) {
    const conversations = this.getConversations();
    const updatedConversations = conversations.filter(conv => conv.sessionId !== sessionId);
    this.saveConversations(updatedConversations);
    
    // Also clear any cached messages for this conversation
    this.clearConversationMessages(sessionId);
    
    // Clear active conversation if it's the one being deleted
    if (this.getActiveConversationId() === sessionId) {
      this.setActiveConversationId(null);
    }
    
    return updatedConversations;
  }

  /**
   * Get a specific conversation
   */
  static getConversation(sessionId) {
    const conversations = this.getConversations();
    return conversations.find(conv => conv.sessionId === sessionId);
  }

  /**
   * Cache messages for a conversation with longer expiry for real sessions
   */
  static cacheConversationMessages(sessionId, messages) {
    try {
      const cacheKey = `messages_${sessionId}`;
      
      // Longer cache time for real sessions (24 hours), shorter for temp sessions (1 hour)
      const cacheHours = sessionId.startsWith('temp-') ? 1 : 24;
      const expiryTime = new Date(Date.now() + cacheHours * 60 * 60 * 1000);
      
      const messageData = {
        sessionId,
        messages,
        cachedAt: new Date().toISOString(),
        expiresAt: expiryTime.toISOString()
      };
      
      localStorage.setItem(cacheKey, JSON.stringify(messageData));
      console.log(`Cached ${messages.length} messages for session ${sessionId} (expires in ${cacheHours}h)`);
      return true;
    } catch (error) {
      console.error('Error caching messages:', error);
      return false;
    }
  }

  /**
   * Get cached messages for a conversation
   */
  static getCachedConversationMessages(sessionId) {
    try {
      const cacheKey = `messages_${sessionId}`;
      const cachedData = localStorage.getItem(cacheKey);
      
      if (!cachedData) {
        console.log(`No cached messages found for session: ${sessionId}`);
        return null;
      }
      
      const messageData = JSON.parse(cachedData);
      
      // Check if cache is expired
      if (new Date() > new Date(messageData.expiresAt)) {
        console.log(`Cache expired for session: ${sessionId}`);
        this.clearConversationMessages(sessionId);
        return null;
      }
      
      console.log(`Retrieved ${messageData.messages.length} cached messages for session: ${sessionId}`);
      return messageData.messages;
    } catch (error) {
      console.error('Error loading cached messages:', error);
      return null;
    }
  }

  /**
   * Clear cached messages for a conversation
   */
  static clearConversationMessages(sessionId) {
    try {
      const cacheKey = `messages_${sessionId}`;
      localStorage.removeItem(cacheKey);
      console.log(`Cleared cached messages for session: ${sessionId}`);
      return true;
    } catch (error) {
      console.error('Error clearing cached messages:', error);
      return false;
    }
  }

  /**
   * Get the active conversation ID
   */
  static getActiveConversationId() {
    return localStorage.getItem(STORAGE_KEYS.ACTIVE_CONVERSATION);
  }

  /**
   * Set the active conversation ID
   */
  static setActiveConversationId(sessionId) {
    if (sessionId) {
      localStorage.setItem(STORAGE_KEYS.ACTIVE_CONVERSATION, sessionId);
    } else {
      localStorage.removeItem(STORAGE_KEYS.ACTIVE_CONVERSATION);
    }
  }

  /**
   * Get user preferences for chat interface
   */
  static getUserPreferences() {
    try {
      const prefs = localStorage.getItem(STORAGE_KEYS.USER_PREFERENCES);
      return prefs ? JSON.parse(prefs) : {
        showConversationsList: true,
        showTasks: false,
        taskFilter: 'all',
        theme: 'default',
        sidebarCollapsed: false
      };
    } catch (error) {
      console.error('Error loading user preferences:', error);
      return {
        showConversationsList: true,
        showTasks: false,
        taskFilter: 'all',
        theme: 'default',
        sidebarCollapsed: false
      };
    }
  }

  /**
   * Save user preferences
   */
  static saveUserPreferences(preferences) {
    try {
      const currentPrefs = this.getUserPreferences();
      const updatedPrefs = { ...currentPrefs, ...preferences };
      localStorage.setItem(STORAGE_KEYS.USER_PREFERENCES, JSON.stringify(updatedPrefs));
      return true;
    } catch (error) {
      console.error('Error saving user preferences:', error);
      return false;
    }
  }

  /**
   * Search conversations by title or content
   */
  static searchConversations(query) {
    const conversations = this.getConversations();
    if (!query.trim()) return conversations;

    const searchTerm = query.toLowerCase();
    return conversations.filter(conv => 
      conv.title.toLowerCase().includes(searchTerm) ||
      conv.sessionId.toLowerCase().includes(searchTerm)
    );
  }

  /**
   * Get conversation statistics
   */
  static getConversationStats() {
    const conversations = this.getConversations();
    const totalMessages = conversations.reduce((sum, conv) => sum + (conv.messageCount || 0), 0);
    
    return {
      totalConversations: conversations.length,
      totalMessages,
      oldestConversation: conversations.length > 0 ? 
        conversations.reduce((oldest, conv) => 
          new Date(conv.createdAt) < new Date(oldest.createdAt) ? conv : oldest
        ) : null,
      mostRecentConversation: conversations.length > 0 ? 
        conversations.reduce((recent, conv) => 
          new Date(conv.lastActivity) > new Date(recent.lastActivity) ? conv : recent
        ) : null
    };
  }

  /**
   * Clean up old conversations (older than specified days)
   */
  static cleanupOldConversations(maxAgeInDays = 30) {
    const conversations = this.getConversations();
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - maxAgeInDays);
    
    const activeConversations = conversations.filter(conv => 
      new Date(conv.lastActivity) > cutoffDate
    );
    
    const removedConversations = conversations.filter(conv => 
      new Date(conv.lastActivity) <= cutoffDate
    );
    
    const removedCount = removedConversations.length;
    
    if (removedCount > 0) {
      this.saveConversations(activeConversations);
      
      // Also clean up cached messages for removed conversations
      removedConversations.forEach(conv => {
        this.clearConversationMessages(conv.sessionId);
      });
      
      console.log(`Cleaned up ${removedCount} old conversations`);
    }
    
    return {
      removedCount,
      remainingCount: activeConversations.length
    };
  }

  /**
   * Export conversation data
   */
  static exportConversations() {
    const conversations = this.getConversations();
    const stats = this.getConversationStats();
    
    // Also export cached messages for each conversation
    const conversationsWithMessages = conversations.map(conv => {
      const messages = this.getCachedConversationMessages(conv.sessionId);
      return {
        ...conv,
        messages: messages || []
      };
    });
    
    return {
      conversations: conversationsWithMessages,
      stats,
      exportedAt: new Date().toISOString(),
      version: '1.1'
    };
  }

  /**
   * Import conversation data
   */
  static importConversations(data) {
    try {
      if (!data.conversations || !Array.isArray(data.conversations)) {
        throw new Error('Invalid conversation data format');
      }
      
      // Merge with existing conversations, avoiding duplicates
      const existingConversations = this.getConversations();
      const existingIds = new Set(existingConversations.map(conv => conv.sessionId));
      
      const newConversations = [];
      let importedMessages = 0;
      
      data.conversations.forEach(conv => {
        if (!existingIds.has(conv.sessionId)) {
          // Extract messages if they exist
          const { messages, ...conversationData } = conv;
          newConversations.push(conversationData);
          
          // Cache messages if they exist
          if (messages && messages.length > 0) {
            this.cacheConversationMessages(conv.sessionId, messages);
            importedMessages += messages.length;
          }
        }
      });
      
      const mergedConversations = [...existingConversations, ...newConversations];
      this.saveConversations(mergedConversations);
      
      return {
        imported: newConversations.length,
        total: mergedConversations.length,
        duplicatesSkipped: data.conversations.length - newConversations.length,
        messagesImported: importedMessages
      };
    } catch (error) {
      console.error('Error importing conversations:', error);
      throw error;
    }
  }

  /**
   * Get storage usage information
   */
  static getStorageInfo() {
    try {
      const conversations = this.getConversations();
      const conversationsSize = new Blob([JSON.stringify(conversations)]).size;
      
      // Calculate cached messages size
      let messagesSize = 0;
      let messageCount = 0;
      conversations.forEach(conv => {
        try {
          const cacheKey = `messages_${conv.sessionId}`;
          const cachedData = localStorage.getItem(cacheKey);
          if (cachedData) {
            messagesSize += new Blob([cachedData]).size;
            const parsed = JSON.parse(cachedData);
            messageCount += parsed.messages ? parsed.messages.length : 0;
          }
        } catch (e) {
          // Ignore errors for individual conversations
        }
      });
      
      const totalSize = conversationsSize + messagesSize;
      
      return {
        conversationsCount: conversations.length,
        cachedMessagesCount: messageCount,
        conversationsSize: this.formatBytes(conversationsSize),
        messagesSize: this.formatBytes(messagesSize),
        totalSize: this.formatBytes(totalSize),
        rawSizes: {
          conversations: conversationsSize,
          messages: messagesSize,
          total: totalSize
        }
      };
    } catch (error) {
      console.error('Error calculating storage info:', error);
      return null;
    }
  }

  /**
   * Format bytes to human readable format
   */
  static formatBytes(bytes, decimals = 2) {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  /**
   * Clear all conversation data
   */
  static clearAllData() {
    try {
      // Get all conversations to clean up their cached messages
      const conversations = this.getConversations();
      
      // Clear all cached messages
      conversations.forEach(conv => {
        this.clearConversationMessages(conv.sessionId);
      });
      
      // Clear main storage
      localStorage.removeItem(STORAGE_KEYS.CONVERSATIONS);
      localStorage.removeItem(STORAGE_KEYS.ACTIVE_CONVERSATION);
      localStorage.removeItem(STORAGE_KEYS.USER_PREFERENCES);
      
      console.log('All conversation data cleared');
      return true;
    } catch (error) {
      console.error('Error clearing all data:', error);
      return false;
    }
  }

  /**
   * Validate and repair conversation data
   */
  static validateAndRepairData() {
    try {
      const conversations = this.getConversations();
      let repaired = false;
      
      const repairedConversations = conversations.map(conv => {
        const repairs = {};
        
        // Ensure required fields exist
        if (!conv.sessionId) {
          repairs.sessionId = `repaired-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
          repaired = true;
        }
        
        if (!conv.title) {
          repairs.title = 'Untitled Conversation';
          repaired = true;
        }
        
        if (!conv.createdAt) {
          repairs.createdAt = new Date().toISOString();
          repaired = true;
        }
        
        if (!conv.lastActivity) {
          repairs.lastActivity = conv.createdAt || new Date().toISOString();
          repaired = true;
        }
        
        if (typeof conv.messageCount !== 'number') {
          repairs.messageCount = 0;
          repaired = true;
        }
        
        if (!conv.userId) {
          repairs.userId = 'unknown';
          repaired = true;
        }
        
        return { ...conv, ...repairs };
      });
      
      if (repaired) {
        this.saveConversations(repairedConversations);
        console.log('Conversation data repaired and saved');
      }
      
      return {
        repaired,
        conversationCount: repairedConversations.length
      };
    } catch (error) {
      console.error('Error validating and repairing data:', error);
      return { repaired: false, error: error.message };
    }
  }
}

export default ConversationStorageService;
